import { Card, CardContent, CardHeader, CardTitle } from '@ordo/ui/card'
import { Separator } from '@ordo/ui/separator'
import { useLiquidPreview } from 'hooks/useLiquidPreview'

import type { TemplateMode } from './template-editor'

function HtmlPreview({ html }: { html: string }) {
  return (
    <iframe
      title="Template preview"
      srcDoc={html}
      sandbox="allow-same-origin"
      className="min-h-75 w-full flex-1 rounded border border-border/50 bg-white"
    />
  )
}

function PreviewContent({ mode, html }: { mode: TemplateMode; html: string }) {
  if (mode === 'html') {
    return <HtmlPreview html={html} />
  }
  return <div className="whitespace-pre-wrap text-sm text-foreground">{html}</div>
}

export function TemplatePreview({
  body,
  subject,
  mode = 'plaintext',
  variables,
  layoutBody,
}: {
  body: string
  /** Template's default email subject — rendered with the same variables. */
  subject?: string
  mode?: TemplateMode
  variables?: Record<string, unknown>
  layoutBody?: string
}) {
  const { html: renderedTemplate, error } = useLiquidPreview(body, variables)
  const { html: renderedSubject } = useLiquidPreview(subject ?? '', variables)

  // Inject rendered template into layout via string replacement to preserve raw HTML/CSS
  const finalHtml =
    layoutBody && renderedTemplate ? layoutBody.replace(/\{\{\s*content\s*\}\}/g, renderedTemplate) : renderedTemplate

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      <CardHeader className="py-3">
        <CardTitle className="text-sm font-medium text-muted-foreground">Preview</CardTitle>
      </CardHeader>
      <Separator />
      <CardContent className="flex min-h-0 flex-1 flex-col overflow-auto pt-4">
        {renderedSubject ? (
          <p className="mb-3 text-sm">
            <span className="font-medium text-muted-foreground">Subject: </span>
            {renderedSubject}
          </p>
        ) : null}
        {error ? (
          <div className="rounded-md bg-destructive/10 px-3 py-2 font-mono text-sm text-destructive">{error}</div>
        ) : finalHtml ? (
          <PreviewContent mode={mode} html={finalHtml} />
        ) : (
          <p className="text-sm text-muted-foreground">Enter a template to see a preview...</p>
        )}
      </CardContent>
    </Card>
  )
}
